import { useState, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useT } from "../../i18n";
import { useRuf } from "../../schale/Ruf";
import { useSitzung } from "../../schale/Sitzung";
import { useTelefon } from "../../schale/geraet";
import { Kurzfassung, Wertzeile, Zeilenblatt, Zweizeiler, type Punkt } from "../../bausteine/Handyformen";
import { Blattvonunten } from "../../bausteine/Blattvonunten";
import { aufgabenFehler, wannText } from "../../bausteine/Vorbereitung";
import { pruefzeileOeffnen } from "../../bausteine/Aufgaben";
import { useAufgabeNehmen, useAufnahmeNehmen, useMangelErledigen } from "../../daten/haken";
import { tagKurz } from "../../daten/einsatzzeit";
import type { Aufgabe } from "../../daten/vorbereitung";
import type { Pruefzeile, StartHakt, StartKarteZeile, Wiedervorlage } from "../../daten/typen";
import "../../stile/telefon/start.css";
import { tageBis } from "../../daten/betriebstag";

/* ══════════════════════════════════════════════════════════════════
   Die Zeilen der Startseite am Telefon — eine Form, und die Handlung im
   Blatt (19.09.2026).

   Luca, nach dem Durchklicken am Handy:

   > *„Was es zu tun gibt, sieht einfach nur kacke formatiert aus. Das
   > müssen schöne einzelne Einträge sein, und man kann das dann übernehmen
   > mit einem kleinen Button oder mit einem Pop-up oder so. Fristen sind
   > nicht anklickbar zu prüfen. Kein Fahrer. Das ist auch alles komisch,
   > und das muss einheitlich sein von der Struktur, sodass die Buttons
   > sauber sind und diese Einträge in der Liste immer gleich hoch sind."*

   ── Was vorher da war ─────────────────────────────────────────────

   Fünf Abschnitte, vier Zeilenformen: „Was ist zu tun?" mit Marke links,
   Gerät in der Mitte und „Übernehmen"/„Ich · Zurück in den Pool" als
   Knöpfe **in** der Zeile (bis 116 px hoch), „Zu prüfen" mit Marke und
   einem Satz darunter, die Frist als flache Zeile ohne Ziel am Telefon,
   „Heute" als Karte mit Marke und abgeschnittenem Kunden, „Was hakt" mit
   großer Zahl links. Die Zeilen stammen aus `bausteine/Aufgaben.tsx`, und
   dort bleiben sie — **der Schreibtisch zeichnet sie weiter**, in seinen
   Karten ist die Spaltenform richtig.

   ── Was jetzt gilt ────────────────────────────────────────────────

   **Jede Zeile ist ein `Zweizeiler`**, 56 px: oben, worum es geht (Gerät,
   Kunde, Vorgang), darunter zuerst die Art in Tinte und dann der
   Zusammenhang, rechts die Zeit — zweistöckig wie auf „Heute"
   (`telefon/heute.css`). Der Punkt trägt die Ampel.

   **Die Handlung steht im Blatt, nicht in der Zeile.** Ein Tipp öffnet es:
   oben ein Satz, was los ist, darunter die Werte, unten die eine Handlung,
   die gerade geht — „Übernehmen", „Zurück in den Pool", „Mangel erledigen"
   — und der Weg dorthin, wo weitergearbeitet wird. Auch die **Frist** und
   **„kein Fahrer"** öffnen ein Blatt: Beide sagten bis heute nicht, worum es
   geht, und die Frist führte am Telefon nirgendwohin.
   ══════════════════════════════════════════════════════════════════ */

const trennzeichen = (t: ReturnType<typeof useT>) => ` ${t("allgemein.trennpunkt")} `;

/** Die zweite Zeile: das erste Stück in Tinte — es ist die Art, die vorher als Marke stand. */
function Unter({ vorn, rest }: { vorn: ReactNode; rest: string }) {
  const t = useT();
  return (
    <>
      <span className="stz-art">{vorn}</span>
      {rest ? `${trennzeichen(t)}${rest}` : ""}
    </>
  );
}

/** Die Zeit rechts, zweistöckig — oben der Wert, darunter leise, wovon er handelt. */
function Wann({ text }: { text: string }) {
  if (!text) return null;
  const teile = text.split(" ");
  let ab = teile.length - 1;
  while (ab > 0 && !/\d/.test(teile[ab] ?? "")) ab -= 1;
  let oben = teile.slice(ab).join(" ");
  let unten = teile.slice(0, ab).join(" ");
  /* „seit 15 Tagen" stand als „15 Tagen" über „seit" — die Präposition
     gehört zum Wert (wie `Wann` in Handyformen, Paket 8 Signal). */
  if (/^(seit|ab|bis|vor|in|um|am|noch)$/i.test(unten)) { oben = text; unten = ""; }
  return (
    <span className="stz-wann">
      <b>{oben}</b>
      {unten ? <small>{unten}</small> : null}
    </span>
  );
}

/** Die Knöpfe unten im Blatt — eine Hauptaktion in Hausgelb, der Rest ruhig, alle gleich breit. */
function Handlungen({ children }: { children: ReactNode }) {
  return <div className="stz-handlungen">{children}</div>;
}

/* ══════════════ Was ist zu tun? ══════════════ */

export function Aufgabenzeile({ a, lenkt }: { a: Aufgabe; lenkt: boolean }) {
  const t = useT();
  const { ich } = useSitzung();
  const [offen, setzeOffen] = useState(false);
  const trenn = trennzeichen(t);
  /* Wer sie hat — dieselbe Rechnung wie `Wer` am Schreibtisch
     (`bausteine/Vorbereitung.tsx`): ein eingeplanter Fahrer hat sie auch,
     wenn niemand sie genommen hat. */
  const eingeplant = !a.genommen && a.bediener.length > 0;
  const ichFahre = eingeplant && (a.meins || a.bediener.some((b) => b.id === ich?.id));
  const beiWem = a.genommen
    ? (a.meins ? t("startTelefon.beiMir") : a.genommen.name)
    : eingeplant
      ? (ichFahre ? t("startTelefon.beiMir") : a.bediener.map((b) => b.name).join(", "))
      : t("startTelefon.frei");
  const meins = a.genommen ? a.meins : ichFahre;
  const punkt: Punkt = a.art === "mangel" || a.ueberfaellig ? "stopp"
    : meins ? "gut"
      : !a.genommen && !eingeplant ? "acht"
        : "ruhig";
  const titel = a.art === "ortsaufnahme" || !a.kennung
    ? a.kunde
    : <><span className="stz-kennung">{a.kennung}</span>{a.typ ? `${trenn}${a.typ}` : ""}</>;
  const rest = [beiWem, a.art === "ortsaufnahme" ? (a.ort || a.ortschaft) : a.kunde].filter(Boolean).join(trenn);
  return (
    <>
      <Zweizeiler
        beiKlick={() => setzeOffen(true)}
        punkt={punkt}
        titel={titel}
        unter={<Unter vorn={t(`aufgaben.art.${a.art}`)} rest={rest} />}
        /* Ohne Termin hat die Ortsaufnahme keinen Tag; mit Termin (0180) schon. */
        rechts={a.art === "ortsaufnahme" && !a.termin ? undefined : <Wann text={wannText(t, a.faelligIso, a.tage)} />}
      />
      <Aufgabenblatt a={a} lenkt={lenkt} offen={offen} schliessen={() => setzeOffen(false)} beiWem={beiWem} meins={meins} />
    </>
  );
}

function Aufgabenblatt({ a, lenkt, offen, schliessen, beiWem, meins }: {
  a: Aufgabe; lenkt: boolean; offen: boolean; schliessen: () => void; beiWem: string; meins: boolean;
}) {
  const t = useT();
  const ruf = useRuf();
  const gehZu = useNavigate();
  const nehmen = useAufgabeNehmen();
  const aufnahme = useAufnahmeNehmen();
  const mangel = useMangelErledigen();
  const laeuft = nehmen.isPending || aufnahme.isPending || mangel.isPending;
  const [fehler, setzeFehler] = useState<ReturnType<typeof aufgabenFehler> | null>(null);
  const trenn = trennzeichen(t);

  const umstellen = (zurueck: boolean, bestaetigt = false) => {
    setzeFehler(null);
    const fertig = {
      onSuccess: () => { ruf(zurueck ? "vorbereitung.zurueck" : "vorbereitung.genommen"); schliessen(); },
      onError: (e: unknown) => setzeFehler(aufgabenFehler(e)),
    };
    if (a.art === "ortsaufnahme" && a.aufnahmeId) {
      aufnahme.mutate({ id: a.aufnahmeId, ...(zurueck ? { wer: null } : {}) }, fertig);
    } else if (a.art !== "ortsaufnahme" && a.belegungId) {
      nehmen.mutate({
        art: a.art, belegungId: a.belegungId, ...(zurueck ? { wer: null } : {}), ...(bestaetigt ? { bestaetigt } : {}),
      }, fertig);
    }
  };

  /* Wo weitergearbeitet wird — dieselben Ziele wie die Zeile am Schreibtisch. */
  const weiter: { wort: string; tun: () => void } | null =
    (a.art === "vorbereitung" || a.art === "mangel") && a.belegungId
      ? { wort: "startTelefon.zurVorbereitung", tun: () => void gehZu({ to: "/einsatz/$belegung/vorbereiten", params: { belegung: a.belegungId! } }) }
      : a.art === "ortsaufnahme" && a.aufnahmeId
        ? { wort: "startTelefon.zurBegehung", tun: () => void gehZu({ to: "/aufnahmen/$id", params: { id: a.aufnahmeId! } }) }
        : a.vorgang
          ? { wort: "startTelefon.zumAuftrag", tun: () => void gehZu({ to: "/auftraege/$nr", params: { nr: a.vorgang } }) }
          : a.belegungId
            ? { wort: "startTelefon.zumEinsatz", tun: () => void gehZu({ to: "/einsatz/$belegung", params: { belegung: a.belegungId! } }) }
            : null;

  const darfNehmen = a.darfNehmen && !meins;
  const darfZurueck = !darfNehmen && a.darfZurueckgeben;

  return (
    <Blattvonunten
      offen={offen}
      schliessen={schliessen}
      titel={a.art === "ortsaufnahme" || !a.kennung ? a.kunde : [a.kennung, a.typ].filter(Boolean).join(trenn)}
      unter={t(`aufgaben.art.${a.art}`)}
    >
      <div className="stz-blatt">
        <Kurzfassung>
          {t(darfNehmen ? "startTelefon.aufgabeFrei" : meins ? "startTelefon.aufgabeMeins" : "startTelefon.aufgabeHat", {
            art: t(`aufgaben.art.${a.art}`), wer: beiWem,
          })}
        </Kurzfassung>
        <Zeilenblatt>
          {a.faelligIso ? <Wertzeile was={t("startTelefon.wasFaellig")}>{wannText(t, a.faelligIso, a.tage)}</Wertzeile> : null}
          {a.kunde ? <Wertzeile was={t("startTelefon.wasKunde")}>{a.kunde}</Wertzeile> : null}
          {a.ort || a.ortschaft ? (
            <Wertzeile was={t("startTelefon.wasOrt")}>{[a.ort, a.ortschaft].filter(Boolean).join(trenn)}</Wertzeile>
          ) : null}
          {a.bediener.length ? (
            <Wertzeile was={t("startTelefon.wasFaehrt")}>{a.bediener.map((b) => b.name).join(", ")}</Wertzeile>
          ) : null}
          <Wertzeile was={t("startTelefon.wasBeiWem")}>{beiWem}</Wertzeile>
          {a.art === "mangel" && a.maengel.length ? (
            <Wertzeile was={t("startTelefon.wasMaengel")}>{a.maengel.join(trenn)}</Wertzeile>
          ) : null}
          {a.vorgang ? <Wertzeile was={t("startTelefon.wasAuftrag")}><span className="stz-kennung">{a.vorgang}</span></Wertzeile> : null}
        </Zeilenblatt>

        {fehler ? <p className="stz-fehler" role="alert">{t(fehler.pfad, fehler.werte)}</p> : null}

        <Handlungen>
          {darfNehmen ? (
            <button type="button" className="handy-knopf" disabled={laeuft} onClick={() => umstellen(false, fehler?.bestaetigbar === true)}>
              {/* D-47: nach dem Hinweis auf den Schein heißt der Knopf „Trotzdem nehmen". */}
              {t(fehler?.bestaetigbar ? "aufgaben.trotzdemNehmen" : "aufgaben.nehmen")}
            </button>
          ) : null}
          {a.art === "mangel" && lenkt && a.belegungId ? (
            <button
              type="button"
              className={darfNehmen ? "handy-knopf leise" : "handy-knopf"}
              disabled={laeuft}
              onClick={() => mangel.mutate(a.belegungId!, {
                onSuccess: () => { ruf("vorbereitung.mangelErledigt"); schliessen(); },
                onError: (e) => setzeFehler(aufgabenFehler(e)),
              })}
            >
              {t("vorbereitung.mangelErledigen")}
            </button>
          ) : null}
          {weiter ? (
            <button
              type="button"
              className={darfNehmen || (a.art === "mangel" && lenkt) ? "handy-knopf leise" : "handy-knopf"}
              onClick={() => { schliessen(); weiter.tun(); }}
            >
              {t(weiter.wort)}
            </button>
          ) : null}
          {darfZurueck ? (
            <button type="button" className="handy-knopf leise" disabled={laeuft} onClick={() => umstellen(true)}>
              {t("aufgaben.zurueck")}
            </button>
          ) : null}
        </Handlungen>
      </div>
    </Blattvonunten>
  );
}

/* ══════════════ Die Frist ══════════════
   Optionsfrist, Qualifikation, Prüftermin. Bis heute führte sie am
   Telefon nur, wenn sie eine Vorgangsnummer trug — die Qualifikation und
   der Prüftermin blieben Text. Jetzt öffnet jede ein Blatt, das sagt,
   worum es geht, und den Weg dorthin, wo sie erledigt wird. */

const FRIST_WEG: Record<string, "/ressourcen"> = { qualifikation: "/ressourcen", pruefung: "/ressourcen" };

export function Fristzeile({ z }: { z: StartKarteZeile }) {
  const t = useT();
  const gehZu = useNavigate();
  const [offen, setzeOffen] = useState(false);
  const was = z.wer_was || (z.wer_was_key ? t(z.wer_was_key) : "");
  const zeit = z.zeit_key ? t(z.zeit_key, { zahl: z.zeit ?? "" }) : (z.zeit ?? "");
  const weg = z.vorgang
    ? () => void gehZu({ to: "/auftraege/$nr", params: { nr: z.vorgang! } })
    : z.art && FRIST_WEG[z.art]
      ? () => void gehZu({ to: FRIST_WEG[z.art!]! })
      : null;
  return (
    <>
      <Zweizeiler
        beiKlick={() => setzeOffen(true)}
        punkt="acht"
        titel={z.kennung}
        unter={<Unter vorn={t("aufgaben.art.frist")} rest={was} />}
        rechts={<Wann text={zeit} />}
      />
      <Blattvonunten offen={offen} schliessen={() => setzeOffen(false)} titel={z.kennung} unter={t("aufgaben.art.frist")}>
        <div className="stz-blatt">
          <Kurzfassung>{t("startTelefon.fristSatz", { was, wann: zeit })}</Kurzfassung>
          <Zeilenblatt>
            <Wertzeile was={t("startTelefon.wasArt")}>{was}</Wertzeile>
            <Wertzeile was={t("startTelefon.wasWann")}>{zeit}</Wertzeile>
            {z.vorgang ? <Wertzeile was={t("startTelefon.wasAuftrag")}><span className="stz-kennung">{z.vorgang}</span></Wertzeile> : null}
          </Zeilenblatt>
          <Handlungen>
            {weg ? (
              <button type="button" className="handy-knopf" onClick={() => { setzeOffen(false); weg(); }}>
                {t(z.vorgang ? "startTelefon.zumAuftrag" : "startTelefon.zurRessource")}
              </button>
            ) : <p className="stz-leise">{t("startTelefon.fristAmSchreibtisch")}</p>}
          </Handlungen>
        </div>
      </Blattvonunten>
    </>
  );
}

/* ══════════════ Zu prüfen ══════════════
   „kein Fahrer", „zurück", „nicht bestätigt", „Unterschrift": Der Satz, was
   fehlt, stand in der Zeile auf zwei Zeilen umgebrochen — jetzt steht er im
   Blatt, und die Hauptaktion sagt, was als Nächstes zu tun ist. */

function tageSeit(tag: string): number | null {
  // In deutschen Kalendertagen, auch aus einem Zeitpunkt (N8-02).
  const bis = tageBis(tag);
  return bis === null ? null : -bis;
}

export function Pruefzeile({ z }: { z: Pruefzeile }) {
  const t = useT();
  const gehZu = useNavigate();
  const [offen, setzeOffen] = useState(false);
  const werte = {
    nr: z.nr, kunde: z.kunde, position: z.position ?? "", grund: z.grund ?? "",
    tag: z.tag ? tagKurz(z.tag) : "",
    // Welche Pflichtfotos fehlen (0173) — „vorne, Tank".
    fotos: (z.blickwinkel ?? []).map((w) => t(`pflichtfoto.blickwinkel.${w}`)).join(", "),
  };
  const endung = z.grund ? "Grund" : "";
  const was = t(`start.zuPruefenWas.${z.art}${endung}`, werte);
  const satz = t(`start.zuPruefenSatz.${z.art}${endung}`, werte);
  // Der Einsatztag, sonst seit wann es liegt (F-02, 24.09.2026).
  const bezug = z.tag ?? z.seit;
  const tage = bezug ? tageSeit(bezug) : null;
  const alter = tage === null || tage < 0 ? ""
    : tage === 0 ? t("start.zuPruefenHeute")
      : tage === 1 ? t("start.zuPruefenGestern")
        : t("start.zuPruefenSeit", { zahl: tage });
  const art = t(`start.zuPruefenArt.${z.art}`);
  return (
    <>
      <Zweizeiler
        beiKlick={() => setzeOffen(true)}
        punkt={z.art === "fahrer_fehlt" ? "stopp" : "acht"}
        titel={z.kunde}
        unter={<Unter vorn={art.charAt(0).toUpperCase() + art.slice(1)} rest={[z.nr, was].filter(Boolean).join(trennzeichen(t))} />}
        rechts={alter ? <Wann text={alter} /> : undefined}
      />
      <Blattvonunten offen={offen} schliessen={() => setzeOffen(false)} titel={z.kunde} unter={z.nr}>
        <div className="stz-blatt">
          <Kurzfassung>{satz}</Kurzfassung>
          <Zeilenblatt>
            <Wertzeile was={t("startTelefon.wasAuftrag")}><span className="stz-kennung">{z.nr}</span></Wertzeile>
            {z.position ? <Wertzeile was={t("startTelefon.wasPosition")}>{z.position}</Wertzeile> : null}
            {alter ? <Wertzeile was={t("startTelefon.wasSeit")}>{alter}</Wertzeile> : null}
          </Zeilenblatt>
          <Handlungen>
            <button
              type="button"
              className="handy-knopf"
              /* Derselbe Weg wie am Schreibtisch: die Unterschrift gleich ins
                 Protokoll, die Werkstatt in die Ressourcen (F-02). */
              onClick={() => { setzeOffen(false); void pruefzeileOeffnen(gehZu, z); }}
            >
              {t(`startTelefon.pruefenWeg.${z.art}`)}
            </button>
          </Handlungen>
        </div>
      </Blattvonunten>
    </>
  );
}

/* ══════════════ Heute (Auszug auf Start) ══════════════ */

export function Heutezeile({ z }: { z: StartKarteZeile }) {
  const t = useT();
  const gehZu = useNavigate();
  const nr = z.vorgang ?? "";
  const art = z.marke_key ? t(z.marke_key) : "";
  return (
    <Zweizeiler
      {...(nr ? { beiKlick: () => void gehZu({ to: "/auftraege/$nr", params: { nr } }) } : { winkel: false })}
      punkt={z.art === "rein" ? "gut" : "ruhig"}
      titel={z.wer_was}
      unter={<Unter vorn={art ? art.charAt(0).toUpperCase() + art.slice(1) : ""} rest={z.kennung} />}
      /* „seit 02.10." statt einer Uhrzeit von damals (N11-02). */
      rechts={<Wann text={z.zeit_key ? t(z.zeit_key, { datum: z.zeit ?? "" }) : (z.zeit ?? "")} />}
    />
  );
}

/* ══════════════ Was hakt ══════════════
   Die große Zahl links war eine vierte Zeilenform. Die Zahl steht jetzt
   rechts, wo in jeder anderen Zeile die Zeit steht — in Mono und kräftig. */

export function Haktzeile({ h }: { h: StartHakt }) {
  const t = useT();
  const gehZu = useNavigate();
  const telefon = useTelefon();
  // Den Kalender gibt es am Telefon nicht (router.tsx) — dann bleibt die Zeile Text.
  const weg = (h.geh === "/kalender" || h.geh === "/planung") && telefon ? undefined : h.geh;
  return (
    <Zweizeiler
      {...(weg ? { beiKlick: () => void gehZu({ to: weg }) } : { winkel: false })}
      punkt={h.stufe}
      titel={t(h.was_key, h.werte)}
      unter={t(h.warum_key, h.werte)}
      rechts={<span className="stz-zahl">{h.zahl}</span>}
    />
  );
}

/* ══════════════ Wiedervorlage ══════════════
   Zwei Knöpfe in der Zeile („Angebot V-…", „Erledigt") machten sie so hoch
   wie drei andere. Jetzt: Zeile, und die beiden im Blatt. */

export function Vorlagezeile({ v, erledigen, laeuft }: {
  v: Wiedervorlage; erledigen: (id: string, fertig: () => void) => void; laeuft: boolean;
}) {
  const t = useT();
  const gehZu = useNavigate();
  const [offen, setzeOffen] = useState(false);
  const trenn = trennzeichen(t);
  const art = t(v.anlass === "nachfassen" ? "wiedervorlage.markeNachfassen" : "wiedervorlage.markeNotiz");
  /* „seit 1 Tagen" (K-25): Gestern ist gestern — dieselben Wörter wie die
     Zeitspalte darunter. */
  const wann = v.faellig
    ? (v.inTagen === -1 ? t("wiedervorlage.seitGestern")
      : t(v.inTagen < 0 ? "wiedervorlage.ueberfaellig" : "wiedervorlage.heute", { tage: -v.inTagen }))
    : v.wann;
  /* **Rechts dieselbe Zeitspalte wie unter „Was ist zu tun?"** (19.09.2026):
     oben der Tag in Mono, darunter leise, wie weit er weg ist. Hier stand
     „heute fällig" als ein Wort in Mono und „23.09.2026" mit Jahr — zwei
     Formen neben einer Liste, deren Zeilen alle „17.09. / seit" tragen.
     Luca: *„Die müssen aber genauso sein wie auch bei ‚Was du zu tun',
     dass die Zeilen eindeutig dieses konsistente Design haben."* */
  const abstand = v.inTagen < -1 ? t("wiedervorlage.ueberfaellig", { tage: -v.inTagen })
    : v.inTagen === -1 ? t("wiedervorlage.seitGestern")
      : v.inTagen === 0 ? t("wiedervorlage.heuteKurz")
        : v.inTagen === 1 ? t("wiedervorlage.morgenKurz")
          : t("wiedervorlage.inTagenKurz", { tage: v.inTagen });
  return (
    <>
      <Zweizeiler
        beiKlick={() => setzeOffen(true)}
        punkt={v.faellig ? (v.inTagen < 0 ? "stopp" : "acht") : "ruhig"}
        titel={v.kunde}
        unter={<Unter vorn={art} rest={[v.ortschaft, v.notiz].filter(Boolean).join(trenn)} />}
        rechts={(
          <span className="stz-wann">
            <b>{tagKurz(v.wannIso)}</b>
            <small>{abstand}</small>
          </span>
        )}
      />
      <Blattvonunten offen={offen} schliessen={() => setzeOffen(false)} titel={v.kunde} unter={art}>
        <div className="stz-blatt">
          {v.notiz ? <Kurzfassung>{v.notiz}</Kurzfassung> : null}
          <Zeilenblatt>
            <Wertzeile was={t("startTelefon.wasWann")}>{wann}</Wertzeile>
            {v.ortschaft ? <Wertzeile was={t("startTelefon.wasOrt")}>{v.ortschaft}</Wertzeile> : null}
            {v.vorgang ? <Wertzeile was={t("startTelefon.wasAuftrag")}><span className="stz-kennung">{v.vorgang}</span></Wertzeile> : null}
          </Zeilenblatt>
          <Handlungen>
            <button type="button" className="handy-knopf" disabled={laeuft} onClick={() => erledigen(v.id, () => setzeOffen(false))}>
              {t("wiedervorlage.erledigt")}
            </button>
            {v.anlass === "nachfassen" && v.vorgang ? (
              <button
                type="button"
                className="handy-knopf leise"
                onClick={() => { setzeOffen(false); void gehZu({ to: "/angebote/$nr", params: { nr: v.vorgang! } }); }}
              >
                {t("wiedervorlage.zumAngebot", { nr: v.vorgang })}
              </button>
            ) : null}
            <button
              type="button"
              className="handy-knopf leise"
              onClick={() => { setzeOffen(false); void gehZu({ to: "/kunden/$nr", params: { nr: v.kundeNr } }); }}
            >
              {t("startTelefon.zurAkte")}
            </button>
          </Handlungen>
        </div>
      </Blattvonunten>
    </>
  );
}
